// Cloud save sync — the signed-in account's settings + stats live in
// D1 (via /api/save) so progress follows the player across devices.
// Policy: pull-and-adopt on sign-in (cloud wins when present; local is
// uploaded when the account has no save yet), debounced push after
// every local save while signed in. Anonymized gameplay data only.

import { authHeader, getAccount } from './auth';

export interface CloudPayload { settings: unknown; stats: unknown }

export async function pullSave(): Promise<{ data: CloudPayload | null; updatedAt?: number } | null> {
  if (!getAccount()) return null;
  try {
    const res = await fetch('/api/save', { headers: authHeader() });
    if (!res.ok) return null;
    const body = await res.json() as { ok: boolean; data: string | null; updatedAt?: number };
    if (!body.ok) return null;
    if (!body.data) return { data: null };
    try {
      return { data: JSON.parse(body.data) as CloudPayload, updatedAt: body.updatedAt };
    } catch {
      return { data: null };
    }
  } catch {
    return null;
  }
}

let pushTimer: number | null = null;

/** Debounced cloud push — call after any local save; collapses bursts
 *  (e.g. slider drags) into one PUT ~2.5s after the last change. */
export function schedulePush(collect: () => CloudPayload): void {
  if (!getAccount()) return;
  if (pushTimer != null) clearTimeout(pushTimer);
  pushTimer = window.setTimeout(() => {
    pushTimer = null;
    void pushNow(collect());
  }, 2500);
}

export async function pushNow(payload: CloudPayload): Promise<boolean> {
  if (!getAccount()) return false;
  try {
    const res = await fetch('/api/save', {
      method: 'PUT',
      headers: { 'content-type': 'application/json', ...authHeader() },
      body: JSON.stringify(payload),
    });
    return res.ok;
  } catch {
    return false;
  }
}
